import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react-dom-interactions';
import {
  signOut,
  useDetailUser,
  useIsOrganization,
  useIsSignedIn,
} from '@zelos/shared/lib/sign.service';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { MouseEventHandler } from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  GnbDropDownItemDiv,
  GnbDropDownSpan,
} from './GnbDropDown';
import { breakpointMedium } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { useBreakpoint } from '@/lib/breakpoint.service';

type ProfileItemType = {
  title: string,
  path: string,
  role: 'route',
  administratorOnly?: boolean,
  notInOrganization?: boolean,
  organizationOnly?: boolean,
} | {
  title: string,
  role: 'click',
  administratorOnly?: boolean,
  notInOrganization?: boolean,
  organizationOnly?: boolean,
};

const profileItems: ProfileItemType[] = [
  {
    title: '어드민 홈페이지',
    path: '/business',
    role: 'route',
    administratorOnly: true,
  },
  {
    title: '프로필',
    path: '/profile',
    role: 'route',
  },
  {
    title: '접수 내역',
    path: '/user-registrations',
    role: 'route',
  },
  {
    title: '쿠폰/응시권 등록',
    path: '/coupon',
    role: 'route',
    notInOrganization: true,
  },
  {
    title: 'APP 관리',
    path: '/applications',
    role: 'route',
    organizationOnly: true,
  },
];

const logoutProfileItem: ProfileItemType = {
  title: '로그아웃',
  role: 'click',
};

const Container = styled.div`
display: flex;
margin-right: -8px;

& > a {
  display: grid;
}
`;

const Content = styled.span<{ blue?: boolean }>`
display: grid;
align-items: center;
padding: 0 8px;
margin: 4px 0;
color: ${({ blue }) => blue ? colors.blue : colors.grey};
cursor: pointer;
font-size: 14px;
font-weight: 400;
line-height: 21px;


@media (max-width: ${breakpointMedium}) {
  color: ${colors.grey};
}
`;

const NameContent = styled.span`
overflow: hidden;
max-width: 20vw;
text-overflow: ellipsis;
white-space: nowrap;
`;

const Dot = styled.span`
display: grid;
place-items: center;

&::after {
  content: "·";
}
`;

const hideUnderBreakpointMedium = css`
display: flex;
align-items: center;

@media (max-width: ${breakpointMedium}) {
  display: none;
}
`;

const afterKeyboardArrowDownUnderBreakpointMedium = css`
@media (max-width: ${breakpointMedium}) {
  column-gap: .5em;
  grid-template-columns: repeat(2, max-content);

  &::after {
    display: block;
    width: 1em;
    height: 1em;
    margin-top: 4px;
    background-image: url("/images/ic-keyboard-arrow-down.svg");
    background-position: center;
    background-repeat: no-repeat;
    background-size: 9px 5.56px;
    content: "";
    opacity: .5;
  }
}
`;

type ProfileDropDownLinkProps = {
  item: ProfileItemType,
  isStaff?: boolean,
  isOrganization?: boolean | null,
  onClick?: () => void,
};

const ProfileDropDownLink = ({
  item,
  isStaff,
  isOrganization,
  onClick,
}: ProfileDropDownLinkProps) => {
  const {
    role,
    title,
    administratorOnly,
    notInOrganization,
    organizationOnly,
  } = item;

  if (!isStaff && administratorOnly) return null;
  if (isOrganization && notInOrganization) return null;
  if (!isOrganization && organizationOnly) return null;

  if (role === 'route')
    return (
      <Link href={item.path}>
        <GnbDropDownSpan>{title}</GnbDropDownSpan>
      </Link>
    );

  if (role === 'click')
    return (
      <GnbDropDownSpan onClick={onClick}>{title}</GnbDropDownSpan>
    );

  throw new ReferenceError(`invalid item role "${role}"`);
};

const Profile = () => {
  const router = useRouter();
  const detailUser = useDetailUser();
  const isOrganization = useIsOrganization();
  const isSignedIn = useIsSignedIn();
  const breakpoint = useBreakpoint();

  const [open, setOpen] = useState(false);
  const [destinationQuery, setDestinationQuery] = useState<string | null>(null);

  const isNarrow = useMemo(
    () => ['sm', 'md'].includes(breakpoint),
    [breakpoint],
  );

  const hrefs = useMemo(() => ({
    find: {
      pathname: '/sign',
      query: {
        destination: destinationQuery,
        page: 'find',
      },
    },
    in: {
      pathname: '/sign',
      query: {
        destination: destinationQuery,
        page: 'in',
      },
    },
  }), [destinationQuery]);

  const {
    context,
    floating,
    reference,
    strategy,
    x,
    y,
  } = useFloating({
    middleware: [shift({ padding: 8 })],
    onOpenChange: setOpen,
    open,
  });

  const {
    getReferenceProps,
    getFloatingProps,
  } = useInteractions([
    useClick(context, {
      ignoreMouse: true,
    }),
    useDismiss(context),
    useHover(context, {
      handleClose: safePolygon(),
      mouseOnly: true,
    }),
  ]);

  const referenceProps = getReferenceProps({ ref: reference });
  const floatingProps = getFloatingProps({ ref: floating });

  useEffect(() => {
    setDestinationQuery(router.asPath);
  }, [router.asPath]);

  const handleClickSignOut = async () => {
    await signOut();
    window.location.replace('/');
  };

  const handleClickDropDown: MouseEventHandler = useCallback(() => {
    window.matchMedia('(hover: none), (pointer: coarse)').matches && setOpen(false);
  }, []);

  return (
    <>
      {isSignedIn === true && (
        <Container>
          <Content
            blue
            css={afterKeyboardArrowDownUnderBreakpointMedium}
            {...referenceProps}
          ><NameContent>{detailUser?.name}님</NameContent>
          </Content>
          <span css={hideUnderBreakpointMedium}>
            <Dot />
            <Content onClick={handleClickSignOut}>로그아웃</Content>
          </span>
        </Container>
      )}
      {isSignedIn === false && (
        <Container>
          <Link href={hrefs.in}>
            <Content>로그인</Content>
          </Link>
          <span css={hideUnderBreakpointMedium}>
            <Dot />
            <Link href={hrefs.find}>
              <Content>회원가입</Content>
            </Link>
          </span>
        </Container>
      )}
      {open && (
        <GnbDropDownItemDiv
          {...floatingProps}
          position={strategy}
          top={y ?? 0}
          left={x ?? 0}
          onClick={handleClickDropDown}
        >{profileItems.map((item) => (
            <ProfileDropDownLink
              key={item.title}
              item={item}
              isOrganization={isOrganization}
              isStaff={detailUser?.isStaff}
            />
          ))
          }
          {isNarrow && isSignedIn && (
            <ProfileDropDownLink
              item={logoutProfileItem}
              onClick={handleClickSignOut}
            />
          )}
        </GnbDropDownItemDiv>
      )}
    </>
  );
};

export default Profile;
